import { useContext, useEffect, useState } from "react";
import Card from "../card/card";
import { PinVerificationStyled } from "./pin-verification.styles";
import {
  USER_APPOINTMENT_ACTIONS,
  UserAppointmentContext,
} from "../../context/user-appointment-context";
import ReactCodeInput from "react-code-input";
import { verifyEmailOtp, verifyNumberOtp } from "../../../../actions";
import { addNotification } from "../../../../store/slices/ui-slice";
import { useAppDispatch } from "../../../../store/hooks";
import { v4 as uuid } from "uuid";
import { setUserInSession } from "../../../../utils/common-utils";

interface PinVerificationPropsType {
  title?: any;
  description?: string | JSX.Element;
  btnTitle?: string;
  email?: string;
  number?: string;
  verificationType?: "number" | "email";
}

const PinVerification = ({
  title,
  description,
  btnTitle,
  email,
  number,
  verificationType,
}: PinVerificationPropsType) => {
  const {
    state: { activeStep },
    userDispatcher,
  } = useContext(UserAppointmentContext);
  const [pinValue, setPinValue] = useState<string>("");
  const [timer, setTimer] = useState<number>(60);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useAppDispatch();

  const handlePinChange = (pin: string) => setPinValue(pin);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const formatTimer = () => {
    const minutes = Math.floor(timer / 60);
    const seconds = timer % 60;
    return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  };

  const onConfirmPin = async () => {
    try {
      let response;
      setIsLoading(true);
      if (verificationType === "email") {
        response = await verifyEmailOtp({
          email: email,
          phone: number,
          otp: pinValue,
        });
        userDispatcher({
          type: USER_APPOINTMENT_ACTIONS.SET_IS_USER_EXISTED,
          payload: !!response?.data?.token,
        });
      }
      if (verificationType === "number") {
        response = await verifyNumberOtp({
          phone: number,
          otp: pinValue,
        });
        userDispatcher({
          type: USER_APPOINTMENT_ACTIONS.SET_IS_USER_EXISTED,
          payload: !!response?.data?.token,
        });
      }

      const token = response?.data?.token;
      if (token) setUserInSession({ userAppToken: token });

      const user = response?.data?.user;
      const name = `${user?.first_name} ${user?.last_name}`;
      const clients = response?.data?.clients || [];
      if (user) {
        userDispatcher({
          type: USER_APPOINTMENT_ACTIONS.SET_USER,
          payload: user,
        });
      }

      userDispatcher({
        type: USER_APPOINTMENT_ACTIONS.SET_CLIENTS_LIST,
        payload: clients,
      });

      // Automatically select the client if only one is returned
      if (clients.length === 1) {
        userDispatcher({
          type: USER_APPOINTMENT_ACTIONS.SET_SELECTED_CLIENT_ACCOUNT,
          payload: clients[0],
        });
        userDispatcher({
          type: USER_APPOINTMENT_ACTIONS.SET_ACTIVE_STEP,
          payload: activeStep + 2, // skip the selection step
        });
      } else {
        userDispatcher({
          type: USER_APPOINTMENT_ACTIONS.SET_ACTIVE_STEP,
          payload: activeStep + 1,
        });
      }
    } catch (err: any) {
      dispatch(
        addNotification({
          id: uuid(),
          type: "error",
          message: err?.message,
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card
      title={title}
      description={description}
      btnTitle={btnTitle || "Continue"}
      onHandleClick={onConfirmPin}
      progress={activeStep * 5}
      disabledConfirmBtn={pinValue.length < 6}
      isLoadingConfirmBtn={isLoading}
    >
      <PinVerificationStyled>
        <ReactCodeInput
          type="number"
          fields={6}
          name="verification"
          inputMode={"numeric"}
          onChange={handlePinChange}
          className="confirm-input-fields"
        />
        <div className="resend-timer">Resend in {formatTimer()}</div>
      </PinVerificationStyled>
    </Card>
  );
};

export default PinVerification;
